/**
 * LocationSelector Component
 *
 * Shared searchable Country + City selector used across all filter sheets.
 * Layout (top → bottom): clean section title → "Use my location" button
 * → Country dropdown → City dropdown. Same component renders identically
 * on Home and Shop, EN and AR.
 *
 * The "Use my location" button delegates to `utils/reverseGeocode` —
 * permission, geocode, slug mapping all live there. This component is
 * purely presentational + state plumbing.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';import { Search, MapPin, ChevronDown, Navigation, CheckCircle2, Loader2 } from 'lucide-react';
import { detectUserLocation } from '../utils/reverseGeocode';
import './LocationSelector.css';

/**
 * SearchableDropdown - Internal component for searchable select
 */
function SearchableDropdown({
  label,
  placeholder,
  items,
  value,
  onChange,
  isRTL,
  disabled,
  testId,
  emptyText,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(i =>
      (i.name_en || '').toLowerCase().includes(q) ||
      (i.name_ar || '').toLowerCase().includes(q)
    );
  }, [items, query]);

  const selectedLabel = useMemo(() => {
    const item = items.find(i => i.key === value);
    if (!item) return '';
    return isRTL ? item.name_ar : item.name_en;
  }, [items, value, isRTL]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (key) => {
    onChange(key === value ? '' : key);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div
      className={`loc-searchable-select ${disabled ? 'loc-searchable-disabled' : ''}`}
      ref={wrapRef}
      data-testid={testId}
    >
      <button
        type="button"
        className={`loc-searchable-trigger ${value ? 'has-value' : ''}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setQuery('');
          }
        }}
        disabled={disabled}
        data-testid={`${testId}-trigger`}
      >
        <span className="loc-searchable-trigger-text">
          {value ? selectedLabel : label}
        </span>
        <ChevronDown size={14} className={`loc-trigger-icon ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="loc-searchable-dropdown" data-testid={`${testId}-dropdown`}>
          <div className="loc-searchable-search-row">
            <Search size={14} className="loc-searchable-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="loc-searchable-search-input"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="loc-searchable-list">
            {filtered.length === 0 ? (
              <div className="loc-searchable-empty">
                {emptyText || (isRTL ? 'لا توجد نتائج' : 'No results')}
              </div>
            ) : (
              filtered.map(item => (
                <button
                  key={item.key}
                  type="button"
                  className={`loc-searchable-item ${item.key === value ? 'active' : ''}`}
                  onClick={() => handleSelect(item.key)}
                  data-testid={`${testId}-item-${item.key}`}
                >
                  {isRTL ? item.name_ar : item.name_en}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * LocationSelector Component
 *
 * @param {Object} props
 * @param {Array} props.countries        full /api/student-hub/countries response
 * @param {Array} props.citiesForCountry cities for the selected country
 * @param {string} props.selectedCountry slug
 * @param {string} props.selectedCity    slug
 * @param {Function} props.onCountryChange
 * @param {Function} props.onCityChange
 * @param {Function} [props.onCoordsResolved] optional — called with {latitude,longitude}
 *                                            when the user grants permission. Shop uses
 *                                            this to enable distance display.
 * @param {boolean} props.isRTL
 * @param {Object} props.labels          custom localized strings (i18n)
 */
export default function LocationSelector({
  countries = [],
  citiesForCountry = [],
  selectedCountry,
  selectedCity,
  onCountryChange,
  onCityChange,
  onCoordsResolved,
  isRTL = false,
  labels = {},
}) {
  const defaultLabels = {
    location: isRTL ? 'الموقع' : 'Location',
    country: isRTL ? 'الدولة' : 'Country',
    city: isRTL ? 'المدينة' : 'City',
    allCities: isRTL ? 'كل المدن' : 'All cities',
    searchCountry: isRTL ? 'ابحث عن دولة...' : 'Search country...',
    searchCity: isRTL ? 'ابحث عن مدينة...' : 'Search city...',
    useMyLocation: isRTL ? 'استخدام موقعي' : 'Use my location',
    detecting: isRTL ? 'جارٍ تحديد موقعك...' : 'Detecting your location...',
    locationDetected: isRTL ? 'تم تحديد موقعك' : 'Location detected',
    cityNotDetected: isRTL
      ? 'تعذّر تحديد المدينة تلقائيًا — يرجى اختيارها يدويًا.'
      : "We couldn't detect your city automatically — please pick it manually.",
    countryNotSupported: isRTL
      ? 'الدولة المكتشفة غير مدعومة — يرجى الاختيار يدويًا.'
      : 'Detected country is not supported — please select manually.',
    permissionDenied: isRTL ? 'تم رفض إذن الموقع' : 'Location permission denied',
    locationUnavailable: isRTL ? 'تعذر تحديد الموقع' : 'Unable to determine location',
    locationTimeout: isRTL ? 'انتهت مهلة تحديد الموقع' : 'Location request timed out',
    geoNotSupported: isRTL ? 'الموقع غير مدعوم على هذا الجهاز' : 'Geolocation not supported on this device',
    noResults: isRTL ? 'لا توجد نتائج' : 'No results',
  };

  const l = { ...defaultLabels, ...labels };

  // Prepend a synthetic "All cities" option whenever a country is selected.
  // Key `__all__` is intentionally never persisted — `handleCitySelect` below
  // maps it back to '' so the backend (which already treats empty city as
  // country-only filter) keeps doing the right thing. Side benefit: countries
  // with sparse city data (e.g. Uganda, Tanzania) now have a usable choice
  // even when their canonical city is the only entry.
  const cityItemsWithAll = useMemo(() => {
    if (!selectedCountry) return [];
    return [
      { key: '__all__', name_en: l.allCities, name_ar: l.allCities },
      ...citiesForCountry,
    ];
  }, [selectedCountry, citiesForCountry, l.allCities]);

  // Translate the synthetic `__all__` key back to '' before bubbling up so
  // every consumer (Home, Shop, saved searches, URL params) keeps the same
  // contract: empty string == no city filter.
  const handleCitySelect = useCallback((cityKey) => {
    onCityChange(cityKey === '__all__' ? '' : cityKey);
  }, [onCityChange]);

  // Local detection state — does not affect parent state until coords resolve.
  const [detecting, setDetecting] = useState(false);
  const [detectMessage, setDetectMessage] = useState(null); // { type: 'ok'|'warn'|'err', text }

  const handleUseMyLocation = useCallback(async () => {
    if (detecting) return;
    setDetecting(true);
    setDetectMessage(null);
    try {
      const result = await detectUserLocation({
        countriesCatalogue: countries,
        language: isRTL ? 'ar' : 'en',
      });

      // Inform Shop / other consumers that need raw coords (e.g. for distance).
      if (typeof onCoordsResolved === 'function' && result.coords) {
        try { onCoordsResolved(result.coords); } catch { /* noop */ }
      }

      if (!result.countryKey) {
        setDetectMessage({ type: 'warn', text: l.countryNotSupported });
        return;
      }

      // Auto-fill country (always overrides — user explicitly tapped).
      onCountryChange(result.countryKey);

      if (result.cityKey) {
        onCityChange(result.cityKey);
        setDetectMessage({ type: 'ok', text: l.locationDetected });
      } else {
        // Country detected, city missing — clear stale city + show note.
        onCityChange('');
        setDetectMessage({ type: 'warn', text: l.cityNotDetected });
      }
    } catch (err) {
      let msg = l.locationUnavailable;
      if (err && err.code === 'UNSUPPORTED') msg = l.geoNotSupported;
      else if (err && err.code === 1) msg = l.permissionDenied;
      else if (err && err.code === 2) msg = l.locationUnavailable;
      else if (err && err.code === 3) msg = l.locationTimeout;
      else if (err && err.code === 'GEOCODE') msg = l.locationUnavailable;
      setDetectMessage({ type: 'err', text: msg });
    } finally {
      setDetecting(false);
    }
  }, [detecting, countries, isRTL, onCountryChange, onCityChange, onCoordsResolved, l]);

  // Note: we intentionally do NOT auto-clear the inline detect message when
  // selectedCountry / selectedCity change — auto-fill triggers those changes
  // itself, and clearing here would race with the success / warn message we
  // just set. The message is overwritten on the next button tap.

  return (
    <div className="loc-selector-container" data-testid="location-selector">
      <label className="loc-selector-label">
        <MapPin size={14} />
        {l.location}
      </label>

      <button
        type="button"
        className={`loc-use-mine-btn ${detecting ? 'is-loading' : ''}`}
        onClick={handleUseMyLocation}
        disabled={detecting}
        data-testid="loc-use-my-location-btn"
        aria-label={l.useMyLocation}
      >
        {detecting ? (
          <Loader2 size={16} className="loc-spin" aria-hidden="true" />
        ) : (
          <Navigation size={16} aria-hidden="true" />
        )}
        <span className="loc-use-mine-text">
          {detecting ? l.detecting : l.useMyLocation}
        </span>
      </button>

      {detectMessage && (
        <div
          className={`loc-detect-msg loc-detect-${detectMessage.type}`}
          role="status"
          data-testid="loc-detect-msg"
        >
          {detectMessage.type === 'ok' && <CheckCircle2 size={14} aria-hidden="true" />}
          <span>{detectMessage.text}</span>
        </div>
      )}

      <SearchableDropdown
        label={l.country}
        placeholder={l.searchCountry}
        items={countries}
        value={selectedCountry}
        onChange={onCountryChange}
        isRTL={isRTL}
        testId="loc-country"
        emptyText={l.noResults}
      />

      <SearchableDropdown
        label={l.city}
        placeholder={l.searchCity}
        items={cityItemsWithAll}
        value={selectedCity}
        onChange={handleCitySelect}
        isRTL={isRTL}
        disabled={!selectedCountry}
        testId="loc-city"
        emptyText={l.noResults}
      />
    </div>
  );
}
